if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.onTriggerMutation = this.syncButton.bind(this);
        this.onPriceMutation = this.syncPrice.bind(this);
        this.trackFrame = this.trackFrame.bind(this);
      }

      connectedCallback() {
        const sectionId = this.dataset.sectionId;

        // The real Add to cart button in the main product form — we proxy to it so
        // variant id, AJAX add, cart drawer and sold-out handling are all reused.
        this.trigger = document.getElementById(`ProductSubmitButton-${sectionId}`);
        this.mainPrice = document.getElementById(`price-${sectionId}`);

        // The header group (announcement bar + header). Optional: the bar works with
        // or without it — see updateOffset(). Never depends on the sticky header.
        this.header = document.querySelector('.section-header');

        this.button = this.querySelector('[data-sticky-atc-button]');
        this.buttonLabel = this.querySelector('[data-sticky-atc-label]');
        this.stickyPrice = this.querySelector('.sticky-atc__price');

        if (!this.trigger || !this.button) return;

        this.button.addEventListener('click', this.forwardClick.bind(this));

        // Mirror the main button's disabled state and label (Add to cart / Sold out).
        this.syncButton();
        this.triggerObserver = new MutationObserver(this.onTriggerMutation);
        this.triggerObserver.observe(this.trigger, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
        });

        // Keep the price in sync with variant changes by mirroring the main price.
        if (this.mainPrice && this.stickyPrice) {
          this.syncPrice();
          this.priceObserver = new MutationObserver(this.onPriceMutation);
          this.priceObserver.observe(this.mainPrice, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        // Set the initial offset before the first reveal to avoid a jump.
        this.updateOffset();

        // Reveal is driven solely by the product form scrolling out of view — the bar
        // exists and toggles independently of whether a sticky header is present.
        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.setVisible(scrolledPast);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(this.trigger);
      }

      disconnectedCallback() {
        cancelAnimationFrame(this.raf);
        this.triggerObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.visibilityObserver?.disconnect();
      }

      forwardClick() {
        if (!this.trigger.disabled) this.trigger.click();
      }

      setVisible(show) {
        if (show === this.visible) return;
        this.visible = show;
        this.classList.toggle('is-visible', show);
        // Track the header's position only while the bar is on screen.
        if (show) {
          this.trackFrame();
        } else {
          cancelAnimationFrame(this.raf);
        }
      }

      trackFrame() {
        this.updateOffset();
        this.raf = requestAnimationFrame(this.trackFrame);
      }

      // Anchor the bar to the bottom of whatever is currently pinned at the top.
      // If the sticky header is disabled, hidden, or scrolled away, that bottom
      // edge is <= 0 and the bar rests flush against the top of the viewport.
      updateOffset() {
        const bottom = this.header ? this.header.getBoundingClientRect().bottom : 0;
        this.style.setProperty('--sticky-atc-top', `${Math.max(0, bottom)}px`);
      }

      syncButton() {
        this.button.disabled = this.trigger.disabled;
        const label = this.trigger.querySelector('span')?.textContent?.trim();
        if (label && this.buttonLabel) this.buttonLabel.textContent = label;
      }

      syncPrice() {
        this.stickyPrice.innerHTML = this.mainPrice.innerHTML;
      }
    }
  );
}
